import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Alert, Switch, FormControlLabel, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { databaseTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DatabaseType } from '../../types';

interface DatabaseTypeFormData { databaseName: string; deploymentType: string; displayOrder: number; isActive: boolean; }

const DatabaseTypes = () => {
  const [types, setTypes] = useState<DatabaseType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingType, setEditingType] = useState<DatabaseType | null>(null);
  const [formData, setFormData] = useState<DatabaseTypeFormData>({ databaseName: '', deploymentType: 'managed', displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try { const response = await databaseTypesAPI.getAll(); setTypes(response.data); setError(''); }
    catch (err) { setError('Failed to load database types'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (type: DatabaseType | null = null) => {
    if (type) { setEditingType(type); setFormData({ databaseName: type.DatabaseName, deploymentType: type.DeploymentType || 'managed', displayOrder: type.DisplayOrder, isActive: !!type.IsActive }); }
    else { setEditingType(null); setFormData({ databaseName: '', deploymentType: 'managed', displayOrder: 0, isActive: true }); }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingType(null); setFormData({ databaseName: '', deploymentType: 'managed', displayOrder: 0, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingType) { await databaseTypesAPI.update(editingType.DatabaseTypeId, formData); } else { await databaseTypesAPI.create(formData); }
      fetchTypes(); handleCloseDialog(); setError('');
    } catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to save database type'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await databaseTypesAPI.delete(id); fetchTypes(); setError(''); } catch (err) { setError('Failed to delete database type'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Database Types</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add Database Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Database Name</TableCell><TableCell>Deployment Type</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.DatabaseTypeId}>
                <TableCell>{type.DatabaseName}</TableCell>
                <TableCell><Chip label={type.DeploymentType === 'vm_based' ? 'Database on VM' : 'Managed/PaaS'} color={type.DeploymentType === 'vm_based' ? 'primary' : 'secondary'} size="small" /></TableCell>
                <TableCell>{type.DisplayOrder}</TableCell><TableCell>{type.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(type)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(type.DatabaseTypeId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? 'Edit Database Type' : 'Add Database Type'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="Database Name (e.g., Azure SQL, SQL Server)" fullWidth value={formData.databaseName} onChange={(e) => setFormData({ ...formData, databaseName: e.target.value })} required />
          <TextField select margin="dense" size="small" label="Deployment Type" fullWidth value={formData.deploymentType} onChange={(e) => setFormData({ ...formData, deploymentType: e.target.value })} helperText="VM-based: Uses CPU/RAM/Edition/Disks. Managed: Uses Cores/Tier only.">
            <MenuItem value="managed">Managed/PaaS (Cores + Tier)</MenuItem>
            <MenuItem value="vm_based">Database on VM (CPU + RAM + Edition + Disks)</MenuItem>
          </TextField>
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.databaseName}>{editingType ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Database Type"
        message="Are you sure you want to deactivate this database type?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default DatabaseTypes;
